// Visa information for the 8 core countries
// Note: This is general information - always verify with official government sources

export interface WorkVisa {
  name: string;
  code?: string;
  description: string;
  duration: string;
  requirements: string[];
  sponsorshipRequired: boolean;
  processingTime?: string;
  cost?: string;
}

export interface DigitalNomadVisa {
  available: boolean;
  name?: string;
  duration?: string;
  requirements?: string[];
  cost?: string;
  incomeRequirement?: string;
  notes?: string;
}

export interface CountryVisaInfo {
  code: string;
  name: string;
  nameKo: string;
  flag: string;
  tourist: {
    name: string;
    duration: string;
    visaRequired: boolean;
    koreanCitizenNote: string;
    requirements?: string[];
  };
  workVisas: WorkVisa[];
  digitalNomad: DigitalNomadVisa;
  usefulLinks: Array<{
    name: string;
    url: string;
  }>;
}

export const VISA_DATA: CountryVisaInfo[] = [
  // United States
  {
    code: 'US',
    name: 'United States',
    nameKo: '미국',
    flag: '🇺🇸',
    tourist: {
      name: 'ESTA / B1-B2 Visa',
      duration: '90 days (ESTA) / 6 months (B1-B2)',
      visaRequired: false,
      koreanCitizenNote: 'Korean citizens can use ESTA (visa waiver) for up to 90 days',
      requirements: [
        'Valid Korean passport (6+ months validity)',
        'Return/onward ticket',
        'Proof of funds',
        'ESTA approval ($21 fee)',
      ],
    },
    workVisas: [
      {
        name: 'H-1B Specialty Occupation',
        code: 'H-1B',
        description: 'For professionals in specialty occupations requiring at least a bachelor\'s degree',
        duration: '3 years (extendable to 6 years)',
        requirements: [
          'Bachelor\'s degree or equivalent',
          'Job offer from US employer',
          'Employer must file petition',
          'Subject to annual lottery (85,000 cap)',
        ],
        sponsorshipRequired: true,
        processingTime: '3-6 months (regular), 15 days (premium)',
        cost: '$1,710 - $4,000+ (employer pays)',
      },
      {
        name: 'L-1 Intracompany Transfer',
        code: 'L-1A/L-1B',
        description: 'For managers/executives (L-1A) or specialized knowledge workers (L-1B) transferring within same company',
        duration: 'L-1A: 7 years, L-1B: 5 years',
        requirements: [
          '1 year employment with foreign company in past 3 years',
          'Manager/executive or specialized knowledge role',
          'US and foreign company must have qualifying relationship',
        ],
        sponsorshipRequired: true,
        processingTime: '2-6 months',
      },
      {
        name: 'E-2 Treaty Investor',
        code: 'E-2',
        description: 'For Korean citizens investing substantial capital in a US business',
        duration: '2 years (indefinitely renewable)',
        requirements: [
          'Korean citizenship (treaty country)',
          'Substantial investment in US business',
          'Must own at least 50% or have operational control',
          'Investment must be "at risk"',
        ],
        sponsorshipRequired: false,
        processingTime: '2-4 months',
        cost: '$315 application fee',
      },
      {
        name: 'O-1 Extraordinary Ability',
        code: 'O-1',
        description: 'For individuals with extraordinary ability in sciences, arts, education, business, or athletics',
        duration: '3 years (extendable)',
        requirements: [
          'Demonstrate extraordinary ability through sustained acclaim',
          'Evidence: awards, publications, high salary, etc.',
          'US employer or agent sponsor',
        ],
        sponsorshipRequired: true,
        processingTime: '2-4 months (regular), 15 days (premium)',
      },
      {
        name: 'EB-2/EB-3 Employment Green Card',
        code: 'EB-2/EB-3',
        description: 'Permanent residency through employer sponsorship',
        duration: 'Permanent',
        requirements: [
          'Job offer from US employer',
          'PERM Labor Certification',
          'EB-2: Master\'s degree or exceptional ability',
          'EB-3: Bachelor\'s degree or skilled worker',
        ],
        sponsorshipRequired: true,
        processingTime: '2-5+ years (backlog for Korean nationals)',
      },
    ],
    digitalNomad: {
      available: false,
      notes: 'No dedicated digital nomad visa. Remote workers typically use B1/B2 tourist visa, but working for US clients while in the US is a gray area. Many use the visa waiver for short stays while working for non-US employers.',
    },
    usefulLinks: [
      { name: 'USCIS Official', url: 'https://www.uscis.gov' },
      { name: 'US Embassy Korea', url: 'https://kr.usembassy.gov' },
      { name: 'ESTA Application', url: 'https://esta.cbp.dhs.gov' },
    ],
  },

  // Canada
  {
    code: 'CA',
    name: 'Canada',
    nameKo: '캐나다',
    flag: '🇨🇦',
    tourist: {
      name: 'eTA (Electronic Travel Authorization)',
      duration: 'Up to 6 months',
      visaRequired: false,
      koreanCitizenNote: 'Korean citizens can apply for eTA online (no visa required)',
      requirements: [
        'Valid Korean passport',
        'eTA ($7 CAD fee)',
        'Return ticket',
        'Proof of funds',
      ],
    },
    workVisas: [
      {
        name: 'Work Permit (LMIA-based)',
        description: 'Standard work permit requiring Labour Market Impact Assessment',
        duration: 'Varies (usually matches job contract)',
        requirements: [
          'Job offer from Canadian employer',
          'Positive LMIA (employer proves no Canadian available)',
          'Relevant qualifications',
          'Medical exam may be required',
        ],
        sponsorshipRequired: true,
        processingTime: '2-4 months',
      },
      {
        name: 'Global Talent Stream',
        code: 'GTS',
        description: 'Fast-track work permit for in-demand tech occupations',
        duration: 'Up to 3 years',
        requirements: [
          'Job offer in eligible occupation (software engineers, etc.)',
          'Employer must be referred by designated partner',
          'Category A or B eligibility',
        ],
        sponsorshipRequired: true,
        processingTime: '2 weeks',
      },
      {
        name: 'IEC Working Holiday',
        code: 'IEC',
        description: 'Work and travel program for young Koreans',
        duration: '12-24 months',
        requirements: [
          'Korean citizen aged 18-30',
          'Valid passport',
          'Proof of funds ($2,500 CAD)',
          'Health insurance',
          'Limited spots (lottery system)',
        ],
        sponsorshipRequired: false,
        processingTime: '1-2 months after selection',
        cost: '$161 CAD',
      },
      {
        name: 'Express Entry',
        description: 'Points-based permanent residence pathway',
        duration: 'Permanent',
        requirements: [
          'Skilled work experience',
          'Language proficiency (English/French)',
          'Education credential assessment',
          'Meet CRS score threshold',
        ],
        sponsorshipRequired: false,
        processingTime: '6 months (after ITA)',
      },
    ],
    digitalNomad: {
      available: false,
      notes: 'Canada has no specific digital nomad visa. Remote workers typically enter as tourists (up to 6 months). Working for a Canadian employer requires a work permit.',
    },
    usefulLinks: [
      { name: 'IRCC Official', url: 'https://www.canada.ca/en/immigration-refugees-citizenship.html' },
      { name: 'Express Entry', url: 'https://www.canada.ca/en/immigration-refugees-citizenship/services/immigrate-canada/express-entry.html' },
      { name: 'IEC Korea', url: 'https://www.canada.ca/en/immigration-refugees-citizenship/services/work-canada/iec.html' },
    ],
  },

  // Mexico
  {
    code: 'MX',
    name: 'Mexico',
    nameKo: '멕시코',
    flag: '🇲🇽',
    tourist: {
      name: 'FMM Tourist Permit',
      duration: 'Up to 180 days',
      visaRequired: false,
      koreanCitizenNote: 'Korean citizens can enter visa-free for tourism (FMM issued on arrival)',
      requirements: [
        'Valid Korean passport (6+ months)',
        'FMM form (free for stays under 7 days, otherwise ~$30 USD)',
        'Return/onward ticket',
        'Proof of funds',
      ],
    },
    workVisas: [
      {
        name: 'Temporary Resident Visa (Work)',
        description: 'For employment with a Mexican company',
        duration: '1-4 years (renewable)',
        requirements: [
          'Job offer from Mexican employer',
          'Employer must apply at INM',
          'Proof of employment relationship',
          'May require specific salary threshold',
        ],
        sponsorshipRequired: true,
        processingTime: '1-3 months',
      },
      {
        name: 'Temporary Resident Visa (Independent)',
        description: 'For those with sufficient income or savings (no employment required)',
        duration: '1-4 years (renewable)',
        requirements: [
          'Monthly income of ~$2,700 USD or savings of ~$45,000 USD',
          'Bank statements for past 12 months',
          'No Mexican employment (remote work for foreign company allowed)',
        ],
        sponsorshipRequired: false,
        processingTime: '1-2 months',
        cost: '~$200 USD',
      },
    ],
    digitalNomad: {
      available: true,
      name: 'Temporary Resident Visa',
      duration: '1-4 years',
      requirements: [
        'Proof of income (~$2,700 USD/month) or savings (~$45,000 USD)',
        'Apply at Mexican consulate in home country',
        'Valid passport',
      ],
      cost: '~$200 USD',
      incomeRequirement: '$2,700 USD/month or $45,000 USD savings',
      notes: 'Mexico is one of the most popular destinations for digital nomads. The Temporary Resident visa allows remote work for foreign employers. Many nomads also use the 180-day tourist permit.',
    },
    usefulLinks: [
      { name: 'INM (Immigration)', url: 'https://www.gob.mx/inm' },
      { name: 'Mexico Embassy Korea', url: 'https://embamex.sre.gob.mx/corea/' },
    ],
  },

  // United Kingdom
  {
    code: 'GB',
    name: 'United Kingdom',
    nameKo: '영국',
    flag: '🇬🇧',
    tourist: {
      name: 'Standard Visitor Visa / ETA',
      duration: 'Up to 6 months',
      visaRequired: false,
      koreanCitizenNote: 'Korean citizens can visit visa-free for up to 6 months. ETA will be required from 2025.',
      requirements: [
        'Valid Korean passport',
        'Return ticket',
        'Proof of funds',
        'Accommodation details',
      ],
    },
    workVisas: [
      {
        name: 'Skilled Worker Visa',
        description: 'Main work visa for skilled professionals',
        duration: 'Up to 5 years (leads to settlement)',
        requirements: [
          'Job offer from licensed sponsor',
          'Minimum salary threshold (usually £26,200+)',
          'English language requirement (B1)',
          'Job on eligible occupation list',
        ],
        sponsorshipRequired: true,
        processingTime: '3 weeks (standard)',
        cost: '£719 - £1,500+ (plus healthcare surcharge)',
      },
      {
        name: 'Global Talent Visa',
        description: 'For leaders and emerging talents in specific fields',
        duration: 'Up to 5 years',
        requirements: [
          'Endorsement from approved body (Tech Nation, etc.)',
          'Exceptional talent or promise in field',
          'No job offer required',
        ],
        sponsorshipRequired: false,
        processingTime: '3-8 weeks',
        cost: '£716 (plus healthcare surcharge)',
      },
      {
        name: 'Scale-up Visa',
        description: 'For workers joining fast-growing UK companies',
        duration: '2 years (extendable)',
        requirements: [
          'Job offer from qualifying scale-up company',
          'Minimum salary £36,300',
          'English language requirement',
        ],
        sponsorshipRequired: true,
        processingTime: '3 weeks',
      },
      {
        name: 'Youth Mobility Scheme',
        description: 'Work and travel for young Koreans',
        duration: '2 years',
        requirements: [
          'Korean citizen aged 18-30',
          'Savings of £2,530',
          'No dependents',
          'Limited to 1,000 places per year (ballot)',
        ],
        sponsorshipRequired: false,
        processingTime: '3 weeks after ballot selection',
        cost: '£298 (plus healthcare surcharge)',
      },
    ],
    digitalNomad: {
      available: false,
      notes: 'UK has no digital nomad visa. Remote workers can visit as tourists for up to 6 months but technically should not be "working" in the UK. The Permitted Paid Engagement visa allows short business visits.',
    },
    usefulLinks: [
      { name: 'UK Visas & Immigration', url: 'https://www.gov.uk/browse/visas-immigration' },
      { name: 'Skilled Worker Visa', url: 'https://www.gov.uk/skilled-worker-visa' },
      { name: 'UK Embassy Korea', url: 'https://www.gov.uk/world/organisations/british-embassy-seoul' },
    ],
  },

  // Australia
  {
    code: 'AU',
    name: 'Australia',
    nameKo: '호주',
    flag: '🇦🇺',
    tourist: {
      name: 'ETA (Electronic Travel Authority)',
      duration: 'Up to 3 months per visit',
      visaRequired: false,
      koreanCitizenNote: 'Korean citizens can apply for ETA online (subclass 601)',
      requirements: [
        'Valid Korean passport',
        'ETA ($20 AUD)',
        'Proof of funds',
        'Return ticket',
      ],
    },
    workVisas: [
      {
        name: 'TSS Visa (Temporary Skill Shortage)',
        code: 'Subclass 482',
        description: 'Employer-sponsored temporary work visa',
        duration: '2-4 years depending on stream',
        requirements: [
          'Job offer from approved sponsor',
          'Occupation on skills list',
          '2 years relevant experience',
          'English language requirement (IELTS 5.0+)',
        ],
        sponsorshipRequired: true,
        processingTime: '1-4 months',
        cost: '$1,455 - $3,035 AUD',
      },
      {
        name: 'Employer Nomination Scheme',
        code: 'Subclass 186',
        description: 'Permanent residence through employer sponsorship',
        duration: 'Permanent',
        requirements: [
          'Nominated by approved employer',
          '3 years relevant work experience',
          'Under 45 years old',
          'Skills assessment for occupation',
        ],
        sponsorshipRequired: true,
        processingTime: '6-12 months',
        cost: '$4,640 AUD',
      },
      {
        name: 'Working Holiday Visa',
        code: 'Subclass 417',
        description: 'Work and travel for young Koreans',
        duration: '12 months (extendable with regional work)',
        requirements: [
          'Korean citizen aged 18-30',
          'Sufficient funds ($5,000 AUD)',
          'No dependent children',
          'Meet health requirements',
        ],
        sponsorshipRequired: false,
        processingTime: '14-30 days',
        cost: '$640 AUD',
      },
      {
        name: 'Global Talent Visa',
        code: 'Subclass 858',
        description: 'For highly skilled individuals in target sectors',
        duration: 'Permanent',
        requirements: [
          'Internationally recognized in target sector',
          'High income threshold (~$175,000 AUD)',
          'Nominator in same field',
          'Sectors: tech, health, energy, etc.',
        ],
        sponsorshipRequired: false,
        processingTime: '1-3 months (priority processing)',
      },
    ],
    digitalNomad: {
      available: false,
      notes: 'Australia has no dedicated digital nomad visa. Remote workers typically use the ETA/tourist visa (3 months max). Working for an Australian employer requires a work visa.',
    },
    usefulLinks: [
      { name: 'Department of Home Affairs', url: 'https://immi.homeaffairs.gov.au' },
      { name: 'SkillSelect (EOI)', url: 'https://immi.homeaffairs.gov.au/visas/working-in-australia/skillselect' },
      { name: 'Australia Embassy Korea', url: 'https://southkorea.embassy.gov.au' },
    ],
  },

  // Indonesia
  {
    code: 'ID',
    name: 'Indonesia',
    nameKo: '인도네시아',
    flag: '🇮🇩',
    tourist: {
      name: 'Visa on Arrival (VOA)',
      duration: '30 days (extendable to 60 days)',
      visaRequired: false,
      koreanCitizenNote: 'Korean citizens can get VOA on arrival at major airports',
      requirements: [
        'Valid Korean passport (6+ months)',
        'VOA fee ($35 USD)',
        'Return ticket',
        'Proof of accommodation',
      ],
    },
    workVisas: [
      {
        name: 'KITAS (Limited Stay Permit)',
        code: 'ITAS',
        description: 'Temporary work permit for employment',
        duration: '1-2 years (renewable)',
        requirements: [
          'Sponsoring company in Indonesia',
          'RPTKA (work permit approval)',
          'IMTA (work permit)',
          'Relevant qualifications/experience',
        ],
        sponsorshipRequired: true,
        processingTime: '1-3 months',
        cost: '$1,200 - $2,500 USD (varies)',
      },
      {
        name: 'Investor KITAS',
        description: 'For investors and business owners',
        duration: '1-2 years (renewable)',
        requirements: [
          'Investment in Indonesian company',
          'Minimum capital requirements',
          'Business license (NIB)',
        ],
        sponsorshipRequired: false,
        processingTime: '2-3 months',
      },
    ],
    digitalNomad: {
      available: true,
      name: 'B211A Remote Worker Visa',
      duration: 'Up to 1 year (6 months + 6 month extension)',
      requirements: [
        'Proof of remote work for foreign company',
        'Health insurance',
        'Proof of income',
        'Passport valid 18+ months',
      ],
      cost: '~$300-500 USD',
      incomeRequirement: 'Not specified (proof of ongoing remote work)',
      notes: 'Indonesia (especially Bali) is a top digital nomad destination. The B211A visa is the most common option for remote workers. Many also use visa runs with the 60-day VOA.',
    },
    usefulLinks: [
      { name: 'Indonesia Immigration', url: 'https://www.imigrasi.go.id' },
      { name: 'Indonesia Embassy Korea', url: 'https://kemlu.go.id/seoul/en' },
      { name: 'Bali Digital Nomad Guide', url: 'https://www.indonesia.travel' },
    ],
  },

  // China
  {
    code: 'CN',
    name: 'China',
    nameKo: '중국',
    flag: '🇨🇳',
    tourist: {
      name: 'L Visa (Tourist)',
      duration: '30-90 days per entry',
      visaRequired: true,
      koreanCitizenNote: 'Korean citizens must apply for a visa before traveling to China',
      requirements: [
        'Valid Korean passport (6+ months)',
        'Completed visa application',
        'Passport photo',
        'Flight and hotel bookings',
        'Visa fee (~$140 USD)',
      ],
    },
    workVisas: [
      {
        name: 'Z Visa (Work)',
        description: 'For those with a work permit in China',
        duration: 'Initially 30 days (convert to residence permit)',
        requirements: [
          'Work permit notification letter',
          'Job offer from Chinese employer',
          'Health check',
          'Degree authentication',
          'Criminal background check',
        ],
        sponsorshipRequired: true,
        processingTime: '1-3 months (for full process)',
        cost: '$140 USD (visa) + work permit fees',
      },
      {
        name: 'R Visa (Talent)',
        description: 'For high-level foreign talents and urgently needed professionals',
        duration: '5-10 years (multiple entry)',
        requirements: [
          'Meet "high-level talent" criteria',
          'Confirmed work unit in China',
          'Recommendation from relevant authority',
        ],
        sponsorshipRequired: true,
        processingTime: '5 working days',
      },
    ],
    digitalNomad: {
      available: false,
      notes: 'China has no digital nomad visa. Remote work while on a tourist visa is technically not permitted. Those wishing to stay long-term should explore business (M) or work (Z) visas.',
    },
    usefulLinks: [
      { name: 'China Visa Application', url: 'https://www.visaforchina.cn' },
      { name: 'China Embassy Korea', url: 'http://kr.china-embassy.gov.cn' },
    ],
  },

  // Singapore
  {
    code: 'SG',
    name: 'Singapore',
    nameKo: '싱가포르',
    flag: '🇸🇬',
    tourist: {
      name: 'Visa-Free Entry',
      duration: '30-90 days',
      visaRequired: false,
      koreanCitizenNote: 'Korean citizens can enter Singapore visa-free for up to 90 days',
      requirements: [
        'Valid Korean passport (6+ months)',
        'Completed arrival card (SG Arrival Card app)',
        'Proof of funds',
        'Return/onward ticket',
      ],
    },
    workVisas: [
      {
        name: 'Employment Pass (EP)',
        description: 'For professionals, managers, and executives',
        duration: 'Up to 2 years (renewable)',
        requirements: [
          'Job offer from Singapore employer',
          'Minimum salary $5,000 SGD (varies by age/experience)',
          'Acceptable qualifications',
          'Pass COMPASS framework assessment',
        ],
        sponsorshipRequired: true,
        processingTime: '3 weeks (online)',
        cost: '$105 SGD (application) + $225 SGD (issuance)',
      },
      {
        name: 'S Pass',
        description: 'For mid-level skilled workers',
        duration: 'Up to 2 years (renewable)',
        requirements: [
          'Job offer from Singapore employer',
          'Minimum salary $3,150 SGD',
          'Relevant qualifications/experience',
          'Employer quota and levy requirements',
        ],
        sponsorshipRequired: true,
        processingTime: '3 weeks',
        cost: '$105 SGD (application) + $225 SGD (issuance)',
      },
      {
        name: 'ONE Pass (Overseas Networks & Expertise)',
        description: 'For top talent in business, arts, sports, academia, or research',
        duration: '5 years',
        requirements: [
          'Earn $30,000 SGD/month or equivalent overseas',
          'Or outstanding achievements in field',
          'No employer sponsor needed',
        ],
        sponsorshipRequired: false,
        processingTime: '4 weeks',
        cost: '$105 SGD (application)',
      },
      {
        name: 'Tech.Pass',
        description: 'For established tech entrepreneurs and leaders',
        duration: '2 years (renewable)',
        requirements: [
          'Leadership role in tech company (valued $500M+ USD)',
          'Or significant tech achievements',
          'Minimum $20,000 SGD monthly salary',
        ],
        sponsorshipRequired: false,
        processingTime: '4-8 weeks',
        cost: '$105 SGD (application)',
      },
    ],
    digitalNomad: {
      available: false,
      notes: 'Singapore has no digital nomad visa and strictly enforces work permit requirements. Remote workers cannot legally work while on a tourist visa. Those interested in Singapore should explore the EP, ONE Pass, or Tech.Pass options.',
    },
    usefulLinks: [
      { name: 'MOM (Ministry of Manpower)', url: 'https://www.mom.gov.sg' },
      { name: 'Employment Pass', url: 'https://www.mom.gov.sg/passes-and-permits/employment-pass' },
      { name: 'Singapore Embassy Korea', url: 'https://www.mfa.gov.sg/seoul' },
    ],
  },
];

// Helper function to get visa info by country code
export function getVisaInfoByCode(code: string): CountryVisaInfo | undefined {
  return VISA_DATA.find(v => v.code === code);
}

// Get countries with digital nomad visas
export function getDigitalNomadCountries(): CountryVisaInfo[] {
  return VISA_DATA.filter(v => v.digitalNomad.available);
}

// Common visa types for job postings
export const VISA_TYPES = [
  { id: 'h1b', name: 'H-1B', country: 'US', description: 'Specialty Occupation' },
  { id: 'l1', name: 'L-1', country: 'US', description: 'Intracompany Transfer' },
  { id: 'e2', name: 'E-2', country: 'US', description: 'Treaty Investor' },
  { id: 'o1', name: 'O-1', country: 'US', description: 'Extraordinary Ability' },
  { id: 'eb2', name: 'EB-2/EB-3', country: 'US', description: 'Green Card' },
  { id: 'skilled_worker', name: 'Skilled Worker', country: 'GB', description: 'UK Work Visa' },
  { id: 'global_talent_uk', name: 'Global Talent', country: 'GB', description: 'UK Talent Visa' },
  { id: 'tss_482', name: 'TSS 482', country: 'AU', description: 'Australia Temp Skill' },
  { id: 'ens_186', name: 'ENS 186', country: 'AU', description: 'Australia PR' },
  { id: 'lmia', name: 'LMIA Work Permit', country: 'CA', description: 'Canada Work Permit' },
  { id: 'express_entry', name: 'Express Entry', country: 'CA', description: 'Canada PR' },
  { id: 'ep', name: 'Employment Pass', country: 'SG', description: 'Singapore EP' },
  { id: 'general', name: 'Work Visa Sponsorship', country: 'ALL', description: 'General sponsorship' },
] as const;

export type VisaTypeId = typeof VISA_TYPES[number]['id'];
